import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

/**
 * @service ExcelService
 * @description
 * Service for handling Excel file generation and export operations.
 * It utilizes 'xlsx' for creating workbook objects from JSON data and 'file-saver' for triggering
 * the browser's file download mechanism.
 *
 * ## Key Responsibilities:
 * 1. **Data Conversion**: Converts JSON data arrays into Excel worksheets.
 * 2. **Workbook Creation**: Assembles worksheets into a valid Excel workbook structure.
 * 3. **File Export**: Generates binary blobs from workbook data and triggers file downloads.
 * 4. **File Saving**: Handles the browser-side saving of generated files with appropriate MIME types.
 *
 * ## Features:
 * - Export JSON to .xlsx
 * - Automatic file extension handling
 * - MIME type management for Excel files
 *
 * @dependencies
 * - file-saver
 * - xlsx
 *
 * @example
 * constructor(private excelService: ExcelService) {}
 *
 * downloadData() {
 *   const data = [{name: 'Test', value: 1}];
 *   this.excelService.exportAsExcelFile(data, 'report');
 * }
 */
@Injectable()
export class ExcelService {

  /**
   * @method constructor
   * @description Initializes the ExcelService.
   */
  constructor() { }

  /**
   * @private
   * @static
   * @readonly
   * @type {string}
   * @description MIME type for Excel files (OpenXML format).
   */
  private static readonly EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  /**
   * @private
   * @static
   * @readonly
   * @type {string}
   * @description File extension for Excel files.
   */
  private static readonly EXCEL_EXTENSION = '.xlsx';

  /**
   * @method exportAsExcelFile
   * @public
   * @description
   * Converts a JSON array to an Excel file and triggers a download.
   *
   * Flow:
   * 1. Converts JSON to a worksheet using XLSX.utils.json_to_sheet.
   * 2. Creates a workbook and appends the worksheet.
   * 3. Writes the workbook to a binary buffer.
   * 4. Calls saveAsExcelFile and saveAsFile to save the file.
   *
   * @param {any[]} json - The data to export.
   * @param {string} excelFileName - The name of the file to be saved (without extension).
   * @returns {void}
   * @side-effect Triggers file download in the browser.
   */
  public exportAsExcelFile(json: any[], excelFileName: string): void {

    const myworksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);
    const myworkbook: XLSX.WorkBook = { Sheets: { 'data': myworksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(myworkbook, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, excelFileName);
    this.saveAsFile(excelBuffer, excelFileName + ExcelService.EXCEL_EXTENSION, ExcelService.EXCEL_TYPE);
  }

  /**
   * @method saveAsExcelFile
   * @public
   * @description
   * Saves a buffer as an Excel file using FileSaver.
   * Appends '_exported' and extension to the filename.
   *
   * @param {any} buffer - The binary data buffer of the Excel file.
   * @param {string} fileName - The base filename.
   * @returns {void}
   * @side-effect Triggers file download.
   */
  public saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    FileSaver.saveAs(data, fileName + '_exported'+ EXCEL_EXTENSION);
  }
  // excel.service.ts
  /**
   * @method saveAsFile
   * @public
   * @description
   * Generic method to save a buffer as a file with a specific type.
   * Handles file extension correction.
   *
   * @param {any} buffer - The binary data buffer.
   * @param {string} fileName - The full filename including extension.
   * @param {string} fileType - The MIME type of the file.
   * @returns {void}
   * @side-effect Triggers file download.
   */
  public saveAsFile(buffer: any, fileName: string, fileType: string): void {
    // Extract the file extension from the provided fileName
    const fileExtension = fileName.split('.').pop();
    // Determine the correct file name based on the file type
    const correctedFileName = fileName.replace('.' + fileExtension, '');
    // Append the correct file extension based on the file type
    const fullFileName = correctedFileName + '.' + fileExtension;
    const data: Blob = new Blob([buffer], { type: fileType });
    FileSaver.saveAs(data, fullFileName);
  }
}
